import { BrandSearchList } from '@/components/BrandSearchList';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { allBrandsState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import logger from '@/util/logger';
import type { Brand, BrandMergePreview } from '@rock/types';
import { normalizeBrandName } from '@rock/util';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { lineNameFromAlias } from '@rock/brands';
import { mergeBrands } from './brandMergeApi';

const log = logger('MergeBrandDialog');

/**
 * "This brand is really that one." Three steps: pick the brand that survives (unless the caller already
 * knows it), read what the merge will touch, type the losing brand's name to confirm. The counts come
 * from a server dry run, so what is confirmed is exactly what runs.
 */
export const MergeBrandDialog: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** The brand that becomes a tombstone. */
    loser: Brand;
    /** The surviving brand, when the caller already knows it (the alias refusal); otherwise picked here. */
    winner?: Brand;
}> = ({ open, onOpenChange, loser, winner: presetWinner }) => {
    const navigate = useNavigate();
    const brands = useAsyncAtomValue(allBrandsState, []);
    const [winner, setWinner] = useState<Brand | undefined>(presetWinner);
    const [preview, setPreview] = useState<BrandMergePreview>();
    const [loading, setLoading] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    // Merge as a product line: the loser was "{winner} {line}" all along ("Hunter Brunswick"). A merge
    // that started as a line resumes as that line — the server refuses the other mode.
    const startedAsLine = loser.mergedInto === presetWinner?.id ? loser.mergedAsLineId : undefined;
    const [asLine, setAsLine] = useState(!!startedAsLine);
    const [lineName, setLineName] = useState('');
    // The line name the preview was asked with: edits re-run it on commit (blur / Enter), not per keystroke.
    const [previewLineName, setPreviewLineName] = useState('');

    useEffect(() => {
        if (!open) return;
        setWinner(presetWinner);
        setPreview(undefined);
        setConfirmText('');
        setAsLine(!!startedAsLine);
    }, [open, presetWinner, startedAsLine]);

    // The obvious line name once a winner is known: the recorded line of a started merge, else the
    // loser's name minus the winner's ("HUNTER BRUNSWICK" into Hunter Fan → "Brunswick"); edited freely.
    useEffect(() => {
        if (!winner) return;
        const recorded = startedAsLine ? winner.lines?.find((line) => line.id === startedAsLine) : undefined;
        const name = recorded?.displayName ?? lineNameFromAlias(winner, loser.canonicalName) ?? '';
        setLineName(name);
        setPreviewLineName(name);
    }, [winner, loser.canonicalName, startedAsLine]);

    // The line the merge is asked for, by name alone so the preview effect keys on a string.
    const requestedLineName = asLine ? previewLineName.trim() : '';
    const requestedLine = requestedLineName ? { displayName: requestedLineName } : undefined;

    // The preview runs as soon as a winner is known — the same call with `dryRun`, in the same mode, so
    // the counts are what the confirm would do.
    useEffect(() => {
        if (!open || !winner) return;
        let cancelled = false;
        setLoading(true);
        mergeBrands({
            loserId: loser.id,
            winnerId: winner.id,
            dryRun: true,
            ...(requestedLineName ? { asLine: { displayName: requestedLineName } } : {}),
        })
            .then((result) => {
                if (cancelled) return;
                if (result.status === 'preview') setPreview(result.preview);
                else if (result.status === 'refused') {
                    toast.error('Merge refused', { description: result.reason });
                    setWinner(undefined);
                }
            })
            .catch((err) => {
                if (cancelled) return;
                log.error('Merge preview failed', { err, loserId: loser.id, winnerId: winner.id });
                toast.error('Unable to preview the merge');
                setWinner(undefined);
            })
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, [open, loser.id, winner, requestedLineName]);

    const confirmed = !!confirmText.trim() && normalizeBrandName(confirmText).key === loser.canonicalKey;

    const handleMerge = async () => {
        if (!winner || !confirmed) return;
        setLoading(true);
        try {
            const result = await mergeBrands({
                loserId: loser.id,
                winnerId: winner.id,
                dryRun: false,
                ...(requestedLine ? { asLine: requestedLine } : {}),
            });
            if (result.status === 'merged') {
                log.info('Merged brand', { loserId: loser.id, winnerId: winner.id, mergeId: result.mergeId });
                toast.success(`Merged ${loser.canonicalName} into ${winner.canonicalName}`, {
                    description: `${result.preview.models} model(s), ${result.preview.recalls} recall(s) and ${result.preview.aliasDocs} alias key(s) moved.`,
                });
                onOpenChange(false);
                navigate(`/products/brands/${encodeURIComponent(winner.id)}`);
            } else if (result.status === 'refused') {
                toast.error('Merge refused', { description: result.reason });
            }
        } catch (err) {
            log.error('Brand merge failed', { err, loserId: loser.id, winnerId: winner.id });
            toast.error('Merge failed part-way', {
                description: 'Run the same merge again to resume; every stage picks up what is left.',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(next) => !loading && onOpenChange(next)}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Merge {loser.canonicalName} into…</DialogTitle>
                    <DialogDescription>
                        Every alias, model, recall, document and match moves to the surviving brand. This brand becomes
                        a junk tombstone that points at it; nothing is deleted.
                    </DialogDescription>
                </DialogHeader>
                {!winner ? (
                    <BrandSearchList
                        brands={brands}
                        exclude={{ [loser.id]: 'This brand' }}
                        disabled={loading}
                        onSelect={setWinner}
                    />
                ) : (
                    <div className="space-y-4">
                        <div className="text-sm">
                            Surviving brand: <span className="font-medium">{winner.canonicalName}</span>
                            {!presetWinner && (
                                <Button
                                    variant="link"
                                    className="h-auto px-2 py-0"
                                    onClick={() => setWinner(undefined)}
                                >
                                    change
                                </Button>
                            )}
                        </div>
                        {preview ? (
                            <PreviewList preview={preview} />
                        ) : (
                            <div className="section-description">Counting…</div>
                        )}
                        <label htmlFor="merge-as-line" className="flex items-center gap-2 text-sm">
                            <Checkbox
                                id="merge-as-line"
                                checked={asLine}
                                onCheckedChange={(checked) => setAsLine(checked === true)}
                                disabled={loading || !!startedAsLine}
                            />
                            Merge as a product line of {winner.displayName ?? winner.canonicalName}
                        </label>
                        {asLine && (
                            <Input
                                value={lineName}
                                onChange={(event) => setLineName(event.target.value)}
                                onBlur={() => setPreviewLineName(lineName)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter') setPreviewLineName(lineName);
                                }}
                                placeholder="Line name (e.g. Brunswick)"
                                disabled={loading || !!startedAsLine}
                                autoComplete="off"
                            />
                        )}
                        <div className="space-y-1">
                            <div className="text-sm">
                                Type <span className="font-mono">{loser.canonicalName}</span> to confirm.
                            </div>
                            <Input
                                value={confirmText}
                                onChange={(event) => setConfirmText(event.target.value)}
                                disabled={loading || !preview}
                                autoComplete="off"
                            />
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleMerge}
                        disabled={
                            !winner ||
                            !preview ||
                            !confirmed ||
                            loading ||
                            (asLine && (!lineName.trim() || lineName.trim() !== previewLineName.trim()))
                        }
                    >
                        {loading ? 'Working…' : 'Merge'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const PreviewList: FC<{ preview: BrandMergePreview }> = ({ preview }) => (
    <ul className="space-y-1 rounded-md border bg-muted/40 p-3 text-sm">
        <Row label="Alias keys repointed" value={preview.aliasDocs} />
        <Row label="Models moved" value={preview.models} />
        {preview.modelsMayRehunt > 0 && (
            <Row label="…of which may re-run a manual hunt" value={preview.modelsMayRehunt} muted />
        )}
        <Row label="Recalls re-gated" value={preview.recalls} />
        {preview.liveRecalls > 0 && (
            <li className="text-muted-foreground">
                {preview.liveRecalls} approved recall(s) will re-apply: {preview.liveRecallTitles.join('; ')}
            </li>
        )}
        {preview.models > 0 && (
            <li className="text-muted-foreground">
                A moved model whose number and type the surviving brand already has merges into that model, units and
                all, rather than standing beside it. Moved models pick up the surviving brand's live recalls and
                coverage manuals, with homeowner notifications on.
            </li>
        )}
        <Row label="Documents re-filed" value={preview.documents} />
        <Row label="Recall matches repointed" value={preview.recallMatches} />
        <Row label="Brand requests repointed" value={preview.brandRequests} />
        {preview.carriesLogo && (
            <li className="text-muted-foreground">The logo carries over to the surviving brand.</li>
        )}
        {preview.line && (
            <li className="text-muted-foreground">
                Names and models carry the <span className="font-medium">{preview.line.displayName}</span> line.
            </li>
        )}
        {preview.droppedKeys.length > 0 && (
            <li className="text-muted-foreground">Bare keys deleted, not carried: {preview.droppedKeys.join(', ')}</li>
        )}
    </ul>
);

const Row: FC<{ label: string; value: number; muted?: boolean }> = ({ label, value, muted }) => (
    <li className={`flex justify-between ${muted ? 'text-muted-foreground' : ''}`}>
        <span>{label}</span>
        <span className="font-medium tabular-nums">{value}</span>
    </li>
);
